import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { ChromePicker } from 'react-color';
import { TextField, InputAdornment, Popper, styled, ClickAwayListener } from '@mui/material';
import ColorPickerSwatch from './ColorPickerSwatch';
const DynamicPopper = styled(Popper) `
&[data-popper-placement^='top'] .chrome-picker {
  top: -6px !important;
}`;
const StyledColorPicker = styled(ChromePicker) `
  &.chrome-picker {
    width: 312px !important;
    position: relative;
    left: -44px;
    top: 4px;
    & > div:nth-of-type(2) {
      padding: 12px 30px 20px !important;
    }
  }
  .flexbox-fix:nth-of-type(1) {
    align-items: center !important;
    gap: 16px;
    & > div:nth-of-type(1) {
      width: 30px !important;
      & > div:nth-of-type(1) {
        width: 24px !important;
        height: 24px !important;
        border-radius: 50% !important;
        border: 1px solid rgba(0, 0, 0, 0.23) !important;
        & > div:nth-of-type(1) {
          box-shadow: none !important;
        }
      }
    }
    & > div:nth-of-type(2) > div:nth-of-type(1) {
      height: 12px !important;
    }
  }
  .hue-horizontal > div div {
    width: 16px !important;
    height: 16px !important;
    border-radius: 50% !important;
    transform: translate(-8px, -2px) !important;
  }
  .flexbox-fix:nth-of-type(2) { display: none !important; }
`;
const INVALID_HEX_CODE_CHARACTER = /[^0-9A-Fa-f#]/g;
const POUND_SYMBOL = /#/g;
/**
 * Sanitize a hex value by removing invalid characters and ensuring an initial # symbol.
 * @param hex - The hex code value to sanitize
 * @returns The sanitized hex code value or else the empty string. Note that even though this value
 * will have illegal characters removed, it may still not have the correct length to match any
 * browser-recognized color format (#RGB, #RGBA, #RRGGBB, or #RRGGBBAA). Call `verifyAndFormatHex`
 * after calling this function to produce a valid #RRGGBB or #RRGGBBAA value.
 */
export const sanitizeHex = (hex) => {
    // Remove any invalid characters from the input
    hex = hex.replace(INVALID_HEX_CODE_CHARACTER, '');
    // allow for empty string
    if (!hex.length)
        return '';
    return `#${hex.replace(POUND_SYMBOL, '').toUpperCase()}`;
};
/**
 * Format a possible color hex code as a valid #RRGGBB or #RRGGBBAA value.
 * @param hex - The possible hex code to format. This value is assumed to have all illegal
 * characters removed, but may not yet have the correct length to match any browser-recognized
 * color format (#RGB, #RGBA, #RRGGBB, or #RRGGBBAA). Call `sanitizeHex` before calling this
 * function to obtain a hex code that meets these assumptions.
 * @param isAlphaEnabled - Whether or not the alpha channel is enabled for this color picker. If the
 * alpha channel is not enabled, then any alpha value in the provided hex code will simply be
 * discarded to produce an opaque color.
 * @returns A color in #RRGGBB or #RRGGBBAA format or else the empty string if the provided hex code
 * did not include enough information to produce a valid #RRGGBB or #RRGGBBAA value.
 */
export const verifyAndFormatHex = (hex, isAlphaEnabled) => {
    let code = hex.replace('#', '');
    if (code.length < 3) {
        // 2 or fewer characters is invalid. Just throw away.
        return '';
    }
    // Convert #RGB and #RGBA to #RRGGBB and #RRGGBBAA, respectively.
    if (code.length <= 4) {
        code = `${code[0]}${code[0]}${code[1]}${code[1]}${code[2]}${code[2]}${code.length === 4 ? `${code[3]}${code[3]}` : ''}`;
    }
    // Handle an odd number of characters by doubling up the last character.
    if ((code.length % 2) === 1) {
        code = `${code}${code[code.length - 1]}`;
    }
    // Truncate based on whether or not alpha is enabled
    return `#${code.slice(0, isAlphaEnabled ? 8 : 6)}`;
};
/**
 * Convert a number to 2 hexadecimal digits.
 * @param val - The number to convert. This number will be constrained to be in the range 0 - 255
 * (inclusive).
 * @returns The padded hexadecimal value including a leading 0 if needed to achieve 2 hexadecimal
 * digits.
 */
const toPaddedHex = (val) => {
    const constrained = Math.max(0, Math.min(255, Math.round(val)));
    return Array(2)
        .fill('0')
        .concat(constrained.toString(16).split(''))
        .slice(-2)
        .join('');
};
/**
 * Convert an RGB color value from ChromePicker to a hex value in #RRGGBB or #RRGGBBAA format.
 * @param rgb - The RGB color value
 * @returns The color in #RRGGBB or #RRGGBBAA format
 */
export const rgbToHex = (rgb) => `#${toPaddedHex(rgb.r)}${toPaddedHex(rgb.g)}${toPaddedHex(rgb.b)}${(rgb.a ?? 1) === 1 ? '' : toPaddedHex(rgb.a * 255)}`.toUpperCase();
const ColorPickerInput = ({ onPickerChange, onInputChange, hexColor, value, alphaEnabled: isAlphaEnabled = false, ...muiTextProps }) => {
    const inputRef = useRef(null);
    const popperRef = useRef(null);
    const ignoreBlurRef = useRef(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if ((!inputRef?.current?.contains(event.target) ||
                document.activeElement !== inputRef.current) &&
                !ignoreBlurRef.current) {
                setAnchorEl(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    const handleInputFocus = (event) => {
        ignoreBlurRef.current = false;
        setAnchorEl(event.currentTarget);
    };
    const handleClickAway = () => {
        if (anchorEl === document.activeElement) {
            return;
        }
        setAnchorEl(null);
    };
    const handleInputBlur = (event) => {
        const formattedHex = verifyAndFormatHex(event.target.value, isAlphaEnabled);
        onInputChange?.(formattedHex);
        if (ignoreBlurRef.current) {
            return;
        }
        setAnchorEl(null);
    };
    const handleInputChange = (event) => {
        const sanitizedInput = sanitizeHex(event.target.value);
        onInputChange?.(sanitizedInput);
    };
    const handleColorChange = ({ rgb }) => {
        ignoreBlurRef.current = true;
        const event = {
            target: {
                // We use the rgb value reported by ChromePicker instead of the hex value b/c the hex value
                // interacts with the alpha channel in surprising ways:
                //   - In general, the hex value does not reflect the alpha channel at all. So we'd have to
                //     get alpha from one of the other color values (like rgb) anyway.
                //   - Unless the color is exactly #00000000. Then the reported hex value is 'transparent',
                //     which isn't very helpful. (It isn't even valid hex!)
                value: rgbToHex(rgb),
            },
        };
        onPickerChange?.(event);
    };
    return (_jsxs(_Fragment, { children: [_jsx(TextField, { ...muiTextProps, id: 'color-picker-input', inputRef: inputRef, onChange: handleInputChange, onFocus: handleInputFocus, onBlur: handleInputBlur, value: value, InputProps: {
                    startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(ColorPickerSwatch, { color: value, enabled: !!value }) })),
                } }), _jsx(ClickAwayListener, { onClickAway: handleClickAway, children: _jsx(DynamicPopper, { id: "color-picker-popper", open: !!anchorEl, ref: popperRef, anchorEl: inputRef.current, placement: "bottom-start", disablePortal: true, sx: { zIndex: 2000 }, children: _jsx(StyledColorPicker, { disableAlpha: !isAlphaEnabled, onChange: handleColorChange, color: hexColor }) }) })] }));
};
export default ColorPickerInput;
//# sourceMappingURL=index.js.map